'use client';

import { useEffect } from 'react';
import { Mail, Phone, CalendarClock } from 'lucide-react';
import { CONTACT_EMAIL_HREF, CONTACT_PHONE, CONTACT_PHONE_TEL } from '@/lib/contact';

interface StickyCtaProps {
  hasOpenHouse: boolean;
}

/**
 * Mobile-only conversion bar pinned to the bottom of listing pages.
 * While mounted it hides the floating contact widget (same job, less clutter).
 */
export default function StickyCta({ hasOpenHouse }: StickyCtaProps) {
  useEffect(() => {
    document.body.dataset.stickyCta = '1';
    return () => {
      delete document.body.dataset.stickyCta;
    };
  }, []);

  return (
    // `listing-sticky-cta` is the hook globals.css uses to get this out of the
    // way while the full-screen gallery owns the viewport (W13 R130b).
    <div
      className="listing-sticky-cta glass-blur fixed inset-x-0 bottom-0 z-40 border-t border-hairline bg-canvas/95 px-4 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3 lg:hidden"
      role="region"
      aria-label="Listing actions"
    >
      <div className="mx-auto flex max-w-md items-center gap-3">
        {CONTACT_PHONE && CONTACT_PHONE_TEL ? (
          <a
            href={CONTACT_PHONE_TEL}
            className="flex h-12 min-h-12 flex-1 touch-manipulation items-center justify-center gap-2 rounded-xl bg-emerald-500 font-semibold text-canvas shadow-glow-emerald transition-colors hover:bg-emerald-400"
          >
            <Phone className="h-4.5 w-4.5" />
            Call now
          </a>
        ) : (
          <a
            href={CONTACT_EMAIL_HREF}
            className="flex h-12 min-h-12 flex-1 touch-manipulation items-center justify-center gap-2 rounded-xl bg-emerald-500 font-semibold text-canvas shadow-glow-emerald transition-colors hover:bg-emerald-400"
          >
            <Mail className="h-4.5 w-4.5" />
            Email us
          </a>
        )}
        <a
          href="#tour-anchor"
          className="glass flex h-12 min-h-12 flex-1 touch-manipulation items-center justify-center gap-2 rounded-xl border border-hairline-strong font-semibold text-ink transition-colors hover:bg-glass-bright"
        >
          <CalendarClock className="h-4.5 w-4.5" />
          {hasOpenHouse ? 'Open house' : 'Request tour'}
        </a>
      </div>
    </div>
  );
}
